import { useMemo } from 'react';
import { Dispatch } from 'redux';
import { useDispatch } from 'react-redux';
import { DialogAction } from '@smart-builder/types';
import { _safe_t } from '@smart-builder/util';
import {
  emitPromptEvent,
  PromptRetryCallback,
  ResponseCodes,
} from '@smart-builder/rest';
import { showDialog, useCloseDialog } from './ducks';
import {
  dialogActionCaptions,
  dialogActionVariants,
  DialogBody,
  DialogElem,
  DialogType,
} from './ducks/types';
import {
  DialogActions,
  DialogOptions,
  DialogResult,
  SimpleDialogCreator,
  StandardDialogCreator,
} from './types';

const dialogAsPromise = (
  dispatch: Dispatch<any>,
  body: DialogBody | undefined,
  bodyOverride: JSX.Element | undefined,
  actions: DialogAction[],
  defaultAutoFocus?: DialogAction,
  options?: DialogOptions,
): Promise<DialogResult> => {
  return new Promise<DialogResult>(onResolve => {
    const autoFocusAction = options?.autoFocusResult ?? defaultAutoFocus;

    const dialog: DialogElem = {
      body,
      bodyOverride,

      buttons: actions.map(action => ({
        action,
        variant: options?.variants?.[action] ?? dialogActionVariants[action],
        caption:
          options?.captions?.[action] ?? _safe_t(dialogActionCaptions[action]),
        autoFocus: autoFocusAction === action,
      })),

      priority: options?.priority ?? 0,
      className: options?.className ?? '',
      closeOnBackground: options?.closeOnBackground ?? true,
      optional: options?.optional,

      onResolve,
    };

    dispatch(showDialog(dialog));
  });
};

export function useDialog(): DialogActions {
  const dispatch = useDispatch();
  const closeDialog = useCloseDialog();

  return useMemo(() => {
    const { Ok, Cancel, Yes, No } = DialogAction;

    const standardDialogCreator =
      (actions: DialogAction[]): StandardDialogCreator =>
      (body, options) =>
        dialogAsPromise(
          dispatch,
          body,
          undefined,
          actions,
          actions[0],
          options,
        );

    const simpleDialogCreator =
      (actions: DialogAction[]): SimpleDialogCreator =>
      (message, options) =>
        dialogAsPromise(
          dispatch,
          { message },
          undefined,
          actions,
          actions[0],
          options,
        );

    return {
      closeDialog,

      dialogOk: standardDialogCreator([Ok]),
      dialogOkCancel: standardDialogCreator([Ok, Cancel]),
      dialogYesNo: standardDialogCreator([Yes, No]),
      dialogYesCancel: standardDialogCreator([Yes, Cancel]),
      dialogYesNoCancel: standardDialogCreator([Yes, No, Cancel]),

      messageOk: simpleDialogCreator([Ok]),
      messageOkCancel: simpleDialogCreator([Ok, Cancel]),
      messageYesNo: simpleDialogCreator([Yes, No]),
      messageYesCancel: simpleDialogCreator([Yes, Cancel]),
      messageYesNoCancel: simpleDialogCreator([Yes, No, Cancel]),

      dialogCustom: (bodyElement, actions, options) =>
        dialogAsPromise(
          dispatch,
          undefined,
          bodyElement,
          actions,
          undefined,
          options,
        ),

      messageCustom: (message, dialogType, actions, options) =>
        dialogAsPromise(
          dispatch,
          { message, dialogType },
          undefined,
          actions,
          actions[0],
          options,
        ),

      dialogError: (
        error: any,
        promptCallback?: PromptRetryCallback,
        onPromptCancel?: () => void,
      ) => {
        if (promptCallback && error?.code === ResponseCodes.sbRequiresPrompt) {
          return new Promise<DialogResult>(resolve => {
            emitPromptEvent({
              type: 'dialog',
              data: error.error.data,
              retryCallback: extraPromptAnswers => {
                promptCallback(extraPromptAnswers);
                resolve({ action: DialogAction.Ok });
              },
              onPromptAbort: onPromptCancel,
            });
          });
        } else {
          return dialogAsPromise(
            dispatch,
            {
              dialogType: DialogType.Error,
              message:
                error.error?.errorMessage ||
                error.errorMessage ||
                _safe_t('There was an unknown server error, please try again'),
              title: _safe_t('Error'),
            },
            undefined,
            [Ok],
            Ok,
            { closeOnBackground: false },
          );
        }
      },
    };
  }, [dispatch, closeDialog]);
}
